import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

// Visualizzatore PDF multipagina: renderizza TUTTE le pagine come canvas scrollabili.
// Affidabile su ogni dispositivo (incluso iPad, dove un PDF in <iframe> mostra solo la prima pagina).
export function PdfBlobViewer({ blob, className = "", zoom = 1 }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!blob) return;
    let cancelled = false;
    let pdfDoc = null;
    (async () => {
      setLoading(true); setError(false);
      try {
        const data = await blob.arrayBuffer();
        pdfDoc = await pdfjsLib.getDocument({ data }).promise;
        const container = containerRef.current;
        if (!container || cancelled) return;
        container.innerHTML = "";
        const dpr = window.devicePixelRatio || 1;
        const targetW = Math.min(820, (container.clientWidth || 760) - 8) * zoom;
        for (let i = 1; i <= pdfDoc.numPages; i++) {
          if (cancelled) return;
          const page = await pdfDoc.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const scale = targetW / base.width;
          const vp = page.getViewport({ scale: scale * dpr });
          const canvas = document.createElement("canvas");
          canvas.width = vp.width; canvas.height = vp.height;
          canvas.style.width = `${vp.width / dpr}px`;
          canvas.style.maxWidth = zoom > 1 ? "none" : "100%";
          canvas.className = "mx-auto mb-3 shadow-sm bg-white rounded";
          canvas.setAttribute("data-testid", `pdf-page-${i}`);
          const ctx = canvas.getContext("2d");
          await page.render({ canvasContext: ctx, viewport: vp }).promise;
          if (cancelled) return;
          container.appendChild(canvas);
        }
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; try { pdfDoc?.destroy?.(); } catch (e) { /* noop */ } };
  }, [blob, zoom]);

  return (
    <div className={`overflow-y-auto bg-slate-100 p-3 ${className}`} data-testid="pdf-blob-viewer">
      {loading && <div className="flex items-center justify-center py-10 text-muted-foreground gap-2"><Loader2 className="animate-spin" size={18} /> Caricamento documento…</div>}
      {error && <div className="text-center py-10 text-destructive text-sm">Impossibile mostrare l'anteprima su questo dispositivo. Usa "Scarica" per aprire il documento.</div>}
      <div ref={containerRef} />
    </div>
  );
}
